import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../hooks/useLanguage';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { SET_CORE_DATA } from 'constants/actions';
import { transformQuestionStructureToFrench } from 'utilities/outcomeQTranslations';

const LanguageToggle = () => {
  const { language, setLanguage } = useLanguage();
  const dispatch = useDispatch();
  const prevLanguageRef = useRef(language);

  const core_data = useSelector(
    (state) => state.audit?.core_data,
    shallowEqual
  );

  const get_survey_questions = useSelector(
    (state) => state.surveyquestions?.survey_questions,
    shallowEqual
  );

  const raw_data = useSelector(
    (state) => state.audit?.raw_data,
    shallowEqual
  );

  // Re-transform category questions when language changes
  useEffect(() => {
    // Only run when language actually changes, not when other dependencies change
    if (prevLanguageRef.current === language) {
      return;
    }

    // Update the ref to the current language
    prevLanguageRef.current = language;

    if (!core_data?.questions || !get_survey_questions || !Array.isArray(get_survey_questions) || get_survey_questions.length === 0) {
      return;
    }

    // Try to find the sort_order from raw_data metadata or by matching question structure
    let sortOrder = null;

    // Priority 1: Get sort_order from raw_data metadata if available
    if (raw_data?.[0]?.datasetMetadata?.selectedSortOrder) {
      sortOrder = raw_data[0].datasetMetadata.selectedSortOrder;
    } else {
      // Priority 2: Try to find it by matching the question structure
      const matchingSurvey = get_survey_questions.find(sq => {
        if (!sq.questions || !core_data.questions) return false;
        // Match by dimension count and structure
        return sq.questions.dimensions?.length === core_data.questions.dimensions?.length;
      });
      if (matchingSurvey) {
        sortOrder = matchingSurvey.sort_order;
      }
    }

    if (!sortOrder) {
      return; // Can't determine which survey to use
    }

    // Get the original question structure for this sort_order
    const SurveyQues = get_survey_questions.find(
      (sq) => sq.sort_order === sortOrder
    );

    if (!SurveyQues || !SurveyQues.questions) {
      return;
    }

    const originalQuestionStructure = {
      title: SurveyQues.questions.title,
      dimensions: SurveyQues.questions.dimensions,
      type: SurveyQues.questions.type,
      type_name: SurveyQues.questions.type_name,
      comments: SurveyQues.questions.comments,
      useLikert: SurveyQues.questions.useLikert,
      analytics: SurveyQues.questions.analytics
    };

    // Transform based on current language
    let transformedQuestions = originalQuestionStructure;
    if (language === 'fr') {
      transformedQuestions = transformQuestionStructureToFrench(originalQuestionStructure);
    }

    // Update core_data with transformed questions
    const updatedCoreData = {
      ...core_data,
      questions: transformedQuestions,
    };

    dispatch({
      type: SET_CORE_DATA,
      payload: updatedCoreData,
    });
  }, [language, core_data, get_survey_questions, raw_data, dispatch]);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'fr' : 'en');
  };

  return (
    <ToggleContainer onClick={toggleLanguage} title={`Switch to ${language === 'en' ? 'French' : 'English'}`}>
      <ToggleButton active={language === 'en'}>
        EN
      </ToggleButton>
      <ToggleButton active={language === 'fr'}>
        FR
      </ToggleButton>
    </ToggleContainer>
  );
};

const ToggleContainer = styled.div`
  display: flex;
  background: white;
  border: 1px solid #DFDFDF;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 12px;
  margin-left: 25px;
  width: calc(100% - 50px);

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  }
`;

const ToggleButton = styled.button`
  padding: 6px 0;
  border: none;
  background: ${props => props.active ? '#2D70E2' : 'white'};
  color: ${props => props.active ? 'white' : '#666D79'};
  font-size: 11px;
  font-weight: 400;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  text-align: center;

  &:hover {
    background: ${props => props.active ? '#2563EB' : '#F8FAFF'};
  }

  &:first-child {
    border-right: 1px solid #DFDFDF;
  }
`;

export default LanguageToggle;

